/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import MediaPrepHeaderSection from "../../../../common/commonComponents/mediaPrepheaderSection";
import MediaPrepPageHeader from "../../../../common/commonComponents/mediaPrepPageHeader";
import { useTranslation } from "react-i18next";
import MBSterilizationHeader from "./partials/MBSterilizationHeader";
import { postAutoclaveServices } from "../../../../../service/postAutoclaveServices";
import { AutoclaveEquipmentsDetail } from "../../../postAutoclaveProcess/emptySterilization/emptySterilization.types";
import { useEffect, useState } from "react";
import {
	ComplianceCheckList,
	MediaSterilization,
} from "../../types/mediaBatchSterilization.types";
import MBSObservedParameters from "./partials/MBSObservedParameters";
import MBSAutoclaveSelection from "./partials/MBSAutoclaveSelection";
import MBSComplianceCheckList from "./partials/MBSComplianceCheckList";
import { MediaBatchPreparationInitServices } from "../../../../../service/mediaBatchPrepInitServices";
import SterilizationProcessEvents from "./partials/SterilizationProcessEvents";
import MBSFooter from "./partials/MBSFooter";
import CustomToast from "../../../../../widgets/toaster";
import { useMediaBatchPrepView } from "../../../../../hooks/useMediaBatchPrepView";

interface Props {
	onNext: () => void;
	onBack: () => void;
	batchId: number;
}

const MediaBatchSterilization: React.FC<Props> = ({ onNext, onBack, batchId }) => {
	const { t } = useTranslation();

	const goBack = () => {
		onBack();
	};

	const goNext = () => {
		onNext();
	}

	const { mediaBatchData, fetchBatchDetails } = useMediaBatchPrepView();
	const readOnly = mediaBatchData?.status !== 'In Preparation';
	const sterilization_details = mediaBatchData?.sterilization_details
	const [autoclaveEquipments, setAutoclaveEquipments] = useState<
		AutoclaveEquipmentsDetail[]
	>([]);
	const [selectedAutoclaveEq, setSelectedEq] =
		useState<AutoclaveEquipmentsDetail | null>(null);
	const [complianceCheckList, setComplianceCheckList] = useState<
		ComplianceCheckList[]
	>([]);

	// If sterilization was already performed, use the existing data from the context;
	// otherwise, use the default values
	const [sterilizationPayload, setSterilizationPayload] =
		useState<MediaSterilization>({
			media_batch_id:  0,
			autoclave_id: "",
			// location_id: "",
			average_pressure_psi:"",
			hold_duration_min: "",
			max_temperature_observed: "",
			cycle_start_datetime: "",
			time_target_temp_attained:"",
			retained_duration_min:0,
			pressure_release_time:"",
			checklist_data: [],
			steam_clox_indicator_result: "",
			file_id: 0,
		});

	const handlePayloadChange = (key: string, value: string | number) => {
		setSterilizationPayload((prev) => ({ ...prev, [key]: value }));
	};
	const targetTemp = mediaBatchData?.powder_consumption[0]?.reference_temperature;

	const [toast, setToast] = useState({
		show: false,
		message: "",
		toastType: "error", // or "error"
	});

	const showToast = (message: string, toastType: "success" | "error") => {
		setToast({ show: true, message, toastType });
	};

	const [errors, setErrors] = useState({
		autoclave: "",
		averagePressure: "",
		holdDuration: "",
		maxTemperature: "",
		cycleStart: "",
		targetTime: "",
		retainedDuration: "",
		pressureRelease: "",
		steamIndicator: "",
		file: "",
	});

	const validateFields = () => {
		const newErrors = {
			autoclave: "",
			averagePressure: "",
			holdDuration: "",
			maxTemperature: "",
			cycleStart: "",
			targetTime: "",
			retainedDuration: "",
			pressureRelease: "",
			steamIndicator: "",
			file: "",
		};

		let valid = true;

		if (!sterilizationPayload.autoclave_id) {
			newErrors.autoclave = "Please select batch";
			valid = false;
		}

		if (!sterilizationPayload.average_pressure_psi) {
			newErrors.averagePressure = "Please enter average pressure";
			valid = false;
		}

		if (!sterilizationPayload.hold_duration_min) {
			newErrors.holdDuration = "Please enter hold duration";
			valid = false;
		}

		if (!sterilizationPayload.max_temperature_observed) {
			newErrors.maxTemperature = "Please enter maximum observed temperature";
			valid = false;
		}

		if (!sterilizationPayload.cycle_start_datetime) {
			newErrors.cycleStart = "Please select cycle start date and time";
			valid = false;
		}

		if (!sterilizationPayload.time_target_temp_attained) {
			newErrors.targetTime = "Please select target temperature attained time";
			valid = false;
		}

		if (!sterilizationPayload.retained_duration_min) {
			newErrors.retainedDuration = "Please enter retained duration";
			valid = false;
		}

		if (!sterilizationPayload.pressure_release_time) {
			newErrors.pressureRelease = "Please select pressure release time";
			valid = false;
		}

		if (!sterilizationPayload.steam_clox_indicator_result) {
			newErrors.steamIndicator = "Please select steam clox indicator result";
			valid = false;
		}

		if (!sterilizationPayload.file_id) {
			newErrors.file = "Please upload file";
			valid = false;
		}

		setErrors(newErrors);
		return valid;
	};

	const handleChecklistChange = (id: number, checked: boolean) => {
		setSterilizationPayload((prev) => {
			const existingIndex = prev.checklist_data.findIndex(
				(item) => item.id === id,
			);
			const status = checked ? "PASS" : "FAIL";

			if (existingIndex !== -1) {
				const updated = [...prev.checklist_data];
				updated[existingIndex] = { id, status };
				return { ...prev, checklist_data: updated };
			} else {
				return {
					...prev,
					checklist_data: [...prev.checklist_data, { id, status }],
				};
			}
		});
	};

	const getAutoclaveEquiments = async () => {
		try {
			const response = await postAutoclaveServices.getAutoclaveEquipments();
			setAutoclaveEquipments(response.data);
		} catch (error) {
			console.error(error);
		}
	};

	// After Getting the checklist populate the sterilization payload with id and status as "FAIL"
	const getCompilanceCheckList = async () => {
		try {
			const response =
				await MediaBatchPreparationInitServices.getComplianceCheckList();
			const checkListComplianceData = response.data
			setComplianceCheckList(checkListComplianceData);
			checkListComplianceData.forEach(item => handleChecklistChange(item.id, false));
		} catch (error) {
			console.error(error);
		}
	};

	const handleAutoclaveChange = (id: string) => {
		const equipment = autoclaveEquipments.find((eq) => eq.id === id);
		setSelectedEq(equipment ?? null);
		handlePayloadChange("autoclave_id", equipment?.id ?? 0);
		// handlePayloadChange("location_id", equipment?.name ?? "");
	};

	useEffect(() => {
		if (batchId) {
			fetchBatchDetails(batchId);
		}
		getAutoclaveEquiments();
		getCompilanceCheckList();
	}, []);

	return (
		<div className="media-prep-scope">
			<MediaPrepHeaderSection />
			<MediaPrepPageHeader pageHeader={t("mediaBatchInit.title")} />
			<div className="flex flex-col h-full bg-[#F8FAFC] min-h-screen">
				<MBSterilizationHeader
				mediaBatchData={mediaBatchData}
					targetTemp={targetTemp}
					equimentRef={selectedAutoclaveEq?.name}
					onBack={goBack}
				/>

				<div className="p-6 flex-1 overflow-y-auto">
					<div className="grid grid-cols-12 gap-6 items-start">
						{/* Main Content Column */}
						<div className="col-span-12 lg:col-span-8 space-y-6">
							{/* 01 Autoclave Selection */}
							<MBSAutoclaveSelection
								autoclaveEquipments={autoclaveEquipments}
								handleAutoclaveChange={handleAutoclaveChange}
								selectedAutoclaveEq={selectedAutoclaveEq}
								sterilizationDetails={sterilization_details}
								isReadOnly={readOnly}
								errors={errors}
								setErrors={setErrors}
							/>

							{/* 02 Observed Parameters (Manual Entry) */}
							<MBSObservedParameters
							sterilizationDetails={sterilization_details}
								isReadOnly={readOnly}
								maxTemp={targetTemp ? targetTemp : "0°C"}
								sterilizationPayload={sterilizationPayload}
								handlePayloadChange={handlePayloadChange}
								errors={errors}
								setErrors={setErrors}
							/>

							{/* 04 Safety & Compliance Checklist */}
							<MBSComplianceCheckList
								isReadOnly={readOnly}
								sterilizationDetails={sterilization_details}
								handleChecklistChange={handleChecklistChange}
								handlePayloadChange={handlePayloadChange}
								sterilizationPayload={sterilizationPayload}
								complianceCheckList={complianceCheckList}
								errors={errors}
								setErrors={setErrors}
							/>
						</div>
						<div className="col-span-12 lg:col-span-4 space-y-6 h-full">
							<SterilizationProcessEvents
								isReadOnly={readOnly}
								sterilizationDetails={sterilization_details}
								targetTemp={targetTemp}
								sterilizationPayload={sterilizationPayload}
								handlePayloadChange={handlePayloadChange}
								errors={errors}
								setErrors={setErrors}
							/>
						</div>
					</div>
				</div>
				{/*Footer*/}
				<MBSFooter
					sterilizationStatus={mediaBatchData?.status}
					isReadonly={readOnly}
					batchID={batchId}
					onBack={goBack}
					onNext={goNext}
					showToast={showToast}
					sterilizationPayload={sterilizationPayload}
					validateFields={validateFields}
				/>
			</div>
			<CustomToast
				show={toast.show}
				message={toast.message}
				toastType={toast.toastType}
				delay={2500}
				onClose={() => setToast((prev) => ({ ...prev, show: false }))}
				toastPosition="topRight" // optional
			/>
		</div>
	);
};

export default MediaBatchSterilization;
